import type { PublicSettingsDto, UserDto } from '@bjj/shared';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from '../api/client';

interface AuthState {
  user: UserDto | null;
  ready: boolean;
  settings: PublicSettingsDto | undefined;
}

const AuthContext = createContext<AuthState>({ user: null, ready: false, settings: undefined });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserDto | null>(api.user);
  const [ready, setReady] = useState(false);
  const settings = useQuery({
    queryKey: ['settings', 'public'],
    queryFn: () => api.request<PublicSettingsDto>('/settings/public'),
    staleTime: 60_000,
  });

  useEffect(() => {
    const unsubscribe = api.subscribe(setUser);
    // Restore the session from the HttpOnly session cookie, if any.
    void api
      .restore()
      .catch(() => null)
      .finally(() => setReady(true));
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const s = settings.data;
    if (!s) return;
    const root = document.documentElement;
    root.style.setProperty('--brand', s.primaryColor);
    root.style.setProperty('--brand-dark', s.secondaryColor);
    document.title = `${s.shortName} · Curriculum Admin`;
  }, [settings.data]);

  return (
    <AuthContext.Provider value={{ user, ready, settings: settings.data }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
